import React, { useState } from 'react';
import logo from '../logo.svg';

const Buchung = () => {
  const today = new Date().toISOString().split('T')[0];

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    street: '',
    zip: '',
    city: '',
    phone: '',
    adults: 1,
    childrenCount: 0,
    accommodationType: 'wohnmobil',
    vehicleLength: '',
    linen: 'nein',
    arrivalDate: '',
    departureDate: ''
  });

  const [childAges, setChildAges] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (name === 'arrivalDate') {
        if (newData.departureDate && newData.departureDate <= value) {
          newData.departureDate = '';
        }
      }
      return newData;
    });
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleChildrenCountChange = (e) => {
    const count = parseInt(e.target.value) || 0;
    setFormData((prev) => ({ ...prev, childrenCount: count }));
    setChildAges((prevAges) => {
      let newAges = [...prevAges];
      if (count > newAges.length) {
        for (let i = newAges.length; i < count; i++) newAges.push('');
      } else {
        newAges = newAges.slice(0, count);
      }
      return newAges;
    });
  };

  const handleAgeChange = (index, value) => {
    const newAges = [...childAges];
    newAges[index] = value;
    setChildAges(newAges);
    if (errors.childAges) {
      setErrors(prev => ({ ...prev, childAges: null }));
    }
  };

  const validateForm = () => {
    let newErrors = {};
    let isValid = true;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      newErrors.email = 'Bitte E-Mail-Adresse angeben.';
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Bitte eine gültige E-Mail-Adresse eingeben.';
      isValid = false;
    }

    if (!formData.arrivalDate) {
      newErrors.arrivalDate = 'Bitte Anreisedatum wählen.';
      isValid = false;
    } else if (formData.arrivalDate < today) {
      newErrors.arrivalDate = 'Anreise darf nicht in der Vergangenheit liegen.';
      isValid = false;
    }

    if (!formData.departureDate) {
      newErrors.departureDate = 'Bitte Abreisedatum wählen.';
      isValid = false;
    } else if (formData.arrivalDate && formData.departureDate <= formData.arrivalDate) {
      newErrors.departureDate = 'Abreise muss nach der Anreise liegen.';
      isValid = false;
    }

    const hasInvalidAge = childAges.some(age => age === '' || parseInt(age) > 15);
    if (formData.childrenCount > 0 && hasInvalidAge) {
      newErrors.childAges = 'Bitte für alle Kinder ein Alter bis max. 15 Jahre angeben. Ältere Personen gelten als Erwachsene.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      const finalData = { ...formData, childAges };
      try {
        const response = await fetch('https://camping-spirkenhof.at/api/booking.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(finalData),
        });
        if (response.ok) {
          setIsSubmitted(true);
        } else {
          const result = await response.json();
          alert('Fehler: ' + result.message);
        }
      } catch (error) {
        console.error('Netzwerkfehler:', error);
        alert('Es gab ein Problem mit der Verbindung zum Server.');
      }
    }
  };

  const needsLength = ['wohnmobil', 'wohnwagen'].includes(formData.accommodationType);
  const needsLinen = formData.accommodationType === 'schlaffass';

  if (isSubmitted) {
    return (
      <div style={styles.container}>
        <div style={styles.logoWrapper}>
          <img src={logo} alt="Campingplatz Logo" style={styles.logo} />
        </div>
        <div style={styles.successBox}>
          <div style={styles.successIcon}>✓</div>
          <h2 style={styles.header}>Anfrage versendet!</h2>
          <p style={{ textAlign: 'center', lineHeight: '1.6' }}>
            Vielen Dank, <strong>{formData.firstName}</strong>!<br />
            Wir haben deine Buchungsanfrage erhalten. Eine Bestätigung wurde soeben an
            <strong> {formData.email}</strong> gesendet.
          </p>
          <p style={{ textAlign: 'center', color: '#666', fontSize: '0.9rem' }}>
            Wir prüfen die Verfügbarkeit und melden uns schnellstmöglich bei dir.
          </p>
          <button
            onClick={() => setIsSubmitted(false)}
            style={{ ...styles.button, backgroundColor: '#666', marginTop: '2rem' }}
          >
            Zurück zum Formular
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>

      <h2 style={styles.header}>Buchungsanfrage</h2>
      <form onSubmit={handleSubmit} style={styles.form}>

        <h3 style={styles.subHeader}>Persönliche Daten</h3>
        <div style={styles.row}>
          <div style={styles.group}>
            <label style={styles.label}>Vorname</label>
            <input required name="firstName" type="text" style={styles.input} onChange={handleChange} />
          </div>
          <div style={styles.group}>
            <label style={styles.label}>Nachname</label>
            <input required name="lastName" type="text" style={styles.input} onChange={handleChange} />
          </div>
        </div>

        <div style={styles.group}>
          <label style={styles.label}>Adresse</label>
          <input required name="street" placeholder="Straße & Hausnr." type="text" style={styles.input} onChange={handleChange} />
        </div>

        <div style={styles.row}>
          <div style={{ ...styles.group, flex: '0 0 30%' }}>
            <input required name="zip" placeholder="PLZ" type="text" style={styles.input} onChange={handleChange} />
          </div>
          <div style={styles.group}>
            <input required name="city" placeholder="Ort" type="text" style={styles.input} onChange={handleChange} />
          </div>
        </div>

        <div style={styles.group}>
          <label style={styles.label}>Telefonnummer</label>
          <input required name="phone" type="tel" style={styles.input} onChange={handleChange} />
        </div>
        <div style={styles.group}>
          <label style={styles.label}>E-Mail-Adresse</label>
          <input
            required
            name="email"
            type="email"
            style={{ ...styles.input, borderColor: errors.email ? 'red' : '#ccc' }}
            onChange={handleChange}
          />
          {errors.email && <span style={styles.errorText}>{errors.email}</span>}
        </div>

        <hr style={styles.divider} />

        <h3 style={styles.subHeader}>Reisedaten</h3>
        <div style={styles.row}>
          <div style={styles.group}>
            <label style={styles.label}>Anreise</label>
            <input
              required
              name="arrivalDate"
              type="date"
              min={today}
              style={{ ...styles.input, borderColor: errors.arrivalDate ? 'red' : '#ccc' }}
              onChange={handleChange}
            />
            {errors.arrivalDate && <span style={styles.errorText}>{errors.arrivalDate}</span>}
          </div>
          <div style={styles.group}>
            <label style={styles.label}>Abreise</label>
            <input
              required
              name="departureDate"
              type="date"
              min={formData.arrivalDate || today}
              style={{ ...styles.input, borderColor: errors.departureDate ? 'red' : '#ccc' }}
              onChange={handleChange}
            />
            {errors.departureDate && <span style={styles.errorText}>{errors.departureDate}</span>}
          </div>
        </div>

        <hr style={styles.divider} />

        <h3 style={styles.subHeader}>Personen</h3>
        <div style={styles.row}>
          <div style={styles.group}>
            <label style={styles.label}>Erwachsene (ab 16 J.)</label>
            <input name="adults" type="number" min="1" value={formData.adults} style={styles.input} onChange={handleChange} />
          </div>
          <div style={styles.group}>
            <label style={styles.label}>Kinder (bis 15 J.)</label>
            <input name="childrenCount" type="number" min="0" value={formData.childrenCount} style={styles.input} onChange={handleChildrenCountChange} />
          </div>
        </div>

        {childAges.length > 0 && (
          <div style={{ ...styles.ageContainer, border: errors.childAges ? '1px solid red' : 'none' }}>
            <p style={{ marginBottom: '0.5rem', fontWeight: 'bold' }}>Alter der Kinder:</p>
            <div style={styles.ageGrid}>
              {childAges.map((age, index) => (
                <div key={index} style={styles.ageGroup}>
                  <label style={{ fontSize: '0.85rem' }}>Kind {index + 1}</label>
                  <input
                    type="number"
                    min="0"
                    max="15"
                    placeholder="Alter"
                    value={age}
                    style={styles.input}
                    onChange={(e) => handleAgeChange(index, e.target.value)}
                    required
                  />
                </div>
              ))}
            </div>
            {errors.childAges && <p style={styles.errorText}>{errors.childAges}</p>}
          </div>
        )}

        <hr style={styles.divider} />

        <h3 style={styles.subHeader}>Unterkunft</h3>
        <div style={styles.group}>
          <select name="accommodationType" value={formData.accommodationType} style={styles.select} onChange={handleChange}>
            <option value="wohnmobil">Wohnmobil</option>
            <option value="wohnwagen">Wohnwagen</option>
            <option value="zelt">Zelt</option>
            <option value="schlaffass">Schlaffass</option>
          </select>
        </div>

        {needsLength && (
          <div style={styles.highlightBox}>
            <label style={styles.label}>Fahrzeuglänge (in Metern)</label>
            <input name="vehicleLength" type="number" step="0.1" placeholder="z.B. 7.5" style={styles.input} onChange={handleChange} required />
          </div>
        )}

        {needsLinen && (
          <div style={styles.highlightBox}>
            <label style={styles.label}>Bettwäsche gewünscht?</label>
            <select name="linen" style={styles.select} onChange={handleChange}>
              <option value="nein">Nein, ich bringe eigene mit</option>
              <option value="ja">Ja, bitte dazu buchen</option>
            </select>
          </div>
        )}

        <button type="submit" style={styles.button}>Jetzt Anfragen</button>

      </form>
    </div>
  );
};

const styles = {
  container: { maxWidth: '640px', margin: '3rem auto', padding: '2.5rem', fontFamily: "'Nunito', Arial, sans-serif", backgroundColor: 'white', borderRadius: '20px', boxShadow: '0 6px 30px rgba(45,106,79,0.12)', border: '1px solid rgba(64,145,108,0.1)' },
  logoWrapper: { display: 'flex', justifyContent: 'center', marginBottom: '1.2rem' },
  logo: { maxWidth: '160px', height: 'auto', borderRadius: '12px' },
  header: { textAlign: 'center', color: '#1b4332', marginBottom: '1.8rem', fontSize: '1.8rem', fontWeight: '800', fontFamily: "'Nunito', Arial, sans-serif" },
  subHeader: { color: '#2d6a4f', borderBottom: '2px solid #d8f3dc', paddingBottom: '0.5rem', marginBottom: '1.1rem', marginTop: '0', fontSize: '1.05rem', fontWeight: '700', fontFamily: "'Nunito', Arial, sans-serif" },
  form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  row: { display: 'flex', gap: '1rem', justifyContent: 'space-between' },
  group: { display: 'flex', flexDirection: 'column', flex: 1 },
  label: { marginBottom: '0.35rem', fontWeight: '700', fontSize: '0.88rem', color: '#2d6a4f', fontFamily: "'Nunito', Arial, sans-serif" },
  input: { padding: '0.7rem 0.9rem', border: '1.5px solid #d8f3dc', borderRadius: '10px', fontSize: '1rem', fontFamily: "'Nunito', Arial, sans-serif", backgroundColor: '#fafffe', transition: 'border-color 0.2s' },
  select: { padding: '0.7rem 0.9rem', border: '1.5px solid #d8f3dc', borderRadius: '10px', fontSize: '1rem', backgroundColor: '#fafffe', fontFamily: "'Nunito', Arial, sans-serif" },
  divider: { border: '0', borderTop: '1px solid #e8f4ec', margin: '0.5rem 0' },
  highlightBox: { backgroundColor: '#f0f7f2', padding: '1.1rem', borderRadius: '12px', border: '1.5px solid #b7e4c7' },
  ageContainer: { backgroundColor: '#fff8e1', padding: '1rem', borderRadius: '12px', marginTop: '0.5rem', border: '1.5px solid #ffe69c' },
  ageGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '10px' },
  ageGroup: { display: 'flex', flexDirection: 'column' },
  button: { marginTop: '1.5rem', padding: '1rem 1.5rem', background: 'linear-gradient(135deg, #2d6a4f, #40916c)', color: 'white', border: 'none', borderRadius: '12px', fontSize: '1.1rem', cursor: 'pointer', fontWeight: '800', fontFamily: "'Nunito', Arial, sans-serif", boxShadow: '0 4px 15px rgba(45,106,79,0.3)', letterSpacing: '0.3px' },
  errorText: { color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem', fontFamily: "'Nunito', Arial, sans-serif" },
  successBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '2rem 1rem' },
  successIcon: { width: '88px', height: '88px', background: 'linear-gradient(135deg, #2d6a4f, #40916c)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', marginBottom: '1.5rem', boxShadow: '0 6px 20px rgba(45,106,79,0.35)' },
};

export default Buchung;
